<template>
  <div class="sales-page">
    <section class="sales-hero">
      <b-container>
        <div v-if="userMarket && customerData" class="seller-card">
          <div class="seller-card__head">
            <div class="seller-card__identity">
              <h1 class="seller-card__title">{{ $t('title.customer.sales') }}</h1>
              <ul class="seller-card__meta">
                <li>
                  <b-icon icon="person-badge" class="mr-1" />
                  {{ $t('pages.sales.tile.user_number') }} {{ customerData.userNumber }}
                </li>
                <li v-if="customerData.shelfNumber">
                  <b-icon icon="grid" class="mr-1" />
                  {{ $t('pages.sales.tile.shelf_number') }} {{ customerData.shelfNumber }}
                </li>
              </ul>
            </div>
            <div v-if="showProductSheets || showBooking" class="seller-card__actions">
              <b-button
                v-if="showProductSheets"
                variant="success"
                @click="$router.push({name: 'product-sheets'})">
                <b-icon icon="tag" class="mr-1" />
                {{ $t('navigation.customer.product_sheets') }}
              </b-button>
              <b-button
                v-if="showBooking"
                variant="primary"
                @click="$router.push({name: 'create-booking'})">
                <b-icon icon="calendar-check" class="mr-1" />
                {{ $t('navigation.customer.create_new_booking') }}
              </b-button>
            </div>
          </div>
          <div class="seller-card__tiles">
            <div class="stat-tile">
              <span class="stat-tile__label">{{ $t('pages.sales.tile.commission') }}</span>
              <span class="stat-tile__value">{{ customerData.fee }}%</span>
              <span class="stat-tile__hint">{{ userMarket.visualName }}</span>
            </div>
            <div
              v-if="userMarket.customerAccessToAccountBalance && userMarket.accountBalanceFeature"
              class="stat-tile"
            >
              <span class="stat-tile__label">{{ $t('pages.sales.tile.account_balance') }}</span>
              <span class="stat-tile__value">{{ customerData.accountBalance }}</span>
            </div>
            <div class="stat-tile stat-tile--wide">
              <span class="stat-tile__label">{{ $t('pages.sales.tile.campaign') }}</span>
              <div v-if="customerData.campaign && !customerData.campaignRequestName">
                <p class="stat-tile__text" v-html="$t('pages.sales.campaign', { campaign_discount: customerData.campaign.discountPercent })"></p>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  @click="showConfirmModal(customerData, 'request-campaign-remove')">
                  {{ $t('table.product_sheets.button_request_campaign_remove') }}
                </b-button>
              </div>
              <div v-else-if="!customerData.campaign && customerData.campaignRequestName">
                <p class="stat-tile__text mb-0" v-html="$t('pages.sales.campaign_request_join', { campaign_discount: customerData.campaignRequestDiscount })"></p>
              </div>
              <div v-else-if="customerData.campaign && customerData.campaignRequestName">
                <p class="stat-tile__text mb-0" v-html="$t('pages.sales.campaign_request_removal', { campaign_discount: customerData.campaignRequestDiscount })"></p>
              </div>
              <div v-else>
                <p class="stat-tile__text" :class="{ 'mb-0': !canRequestCampaign }" v-html="$t('pages.sales.no_active_campaign')"></p>
                <b-button
                  v-if="canRequestCampaign"
                  size="sm"
                  variant="outline-primary"
                  @click="$bvModal.show('request-campaign-add')">
                  {{ $t('table.product_sheets.button_request_campaign_add') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </b-container>
    </section>

    <b-container class="sales-page__body">
      <b-row>
        <b-col lg="8">
          <div v-if="userMarket && customerData">
            <market-card :title="$t('title.customer.sales_dashboard')" class="mb-4">
              <content-table
                fixed
                :fields="fields"
                :items="userSaleLineSummary"
                :slots="templates"
                head-variant="dark"
              >
                <template slot="amount" slot-scope="data">
                  <div>
                    {{ formatNumber(data.tbl.item.amountTotal, { precision: 2, acceptZero: true, marketCountry: userMarket?.supportCountry?.code }) }}
                  </div>
                </template>
                <template slot="quantity" slot-scope="data">
                  <div>
                    {{ formatNumber(data.tbl.item.quantityTotal, { precision: 2, acceptZero: true, marketCountry: userMarket?.supportCountry?.code }) }}
                  </div>
                </template>
                <template slot="share" slot-scope="data">
                  <div>
                    {{ formatNumber(data.tbl.item.shareTotal, { precision: 2, acceptZero: true, marketCountry: userMarket?.supportCountry?.code }) }}
                  </div>
                </template>
                <template slot="period" slot-scope="data">
                  <div>
                    {{ getObjectByKeyValue(salesSummaryPeriods, 'code', data.tbl.item.period).name }}
                  </div>
                </template>
              </content-table>
              <span v-if="!loader.isActive && !userSaleLineSummary.length" class="no-table-data mt-3">
                {{ $t('table.general.no_data') }}
              </span>
            </market-card>

            <market-card :title="$t('title.customer.sale_lines')" class="mb-4">
              <sales-filter
                class="mb-3"
                @filter-applied="updateSaleLinesWithFilter"
                v-if="Object.keys(customerData).length"
                :start="saleFilters.dateStart"
                :end="saleFilters.dateEnd"/>
              <b-card
                class="mb-3 sale-day"
                v-for="(saleList, date, index) in userSaleLines"
                :key="date"
                no-body
                header-tag="header"
                footer-class="total-sale-footer-container"
              >
                <template #header>
                  <h6 class="mb-0">{{ $t('table.sales.title_date_of_sale', { date: formatDate(date, userMarket?.supportCountry?.code, userMarket?.timezone) }) }}</h6>
                </template>
                <b-list-group flush v-for="(saleLine, key) in saleList" :key="`${key}A`">
                  <!--We have to render each sale line by each quantity-->
                  <!--We add "|| 1", because return sale lines have 0 quantity, -->
                  <!--but we still need to render it-->
                  <b-list-group-item class="d-flex justify-content-between" v-for="i in (Math.abs(parseInt(saleLine.quantity)) || 1)" :key="`${i}B`">
                    <span v-if="saleLine.product && saleLine.product.name">
                      {{ `${saleLine.product.sheetProductId}: ${saleLine.product.name}` }}
                      {{ saleLine.displayDiscountPercent ? $t('table.sales.discount_percent_text', { discountPercent: formatNumber(saleLine.displayDiscountPercent, { precision: 1, marketCountry: userMarket?.supportCountry?.code }) }) : '' }}
                    </span>
                    <span v-else-if="saleLine.product">
                      {{ `${saleLine.product.sheetProductId}: ${saleLine.productType === usedProductType ? $t('table.sales.generic_used_product_name') : $t('table.sales.generic_new_product_name')}` }}
                      {{ saleLine.displayDiscountPercent ? $t('table.sales.discount_percent_text', { discountPercent: formatNumber(saleLine.displayDiscountPercent, { precision: 1, marketCountry: userMarket?.supportCountry?.code }) }) : '' }}
                    </span>
                    <span v-else>
                      {{ saleLine.productType === usedProductType ? $t('table.sales.generic_used_product_name') : $t('table.sales.generic_new_product_name') }}
                      {{ saleLine.displayDiscountPercent ? $t('table.sales.discount_percent_text', { discountPercent: formatNumber(saleLine.displayDiscountPercent, { precision: 1, marketCountry: userMarket?.supportCountry?.code }) }) : '' }}
                    </span>
                    <span>{{ formatNumber(saleLine.pricePerPiece, { precision: 2, marketCountry: userMarket?.supportCountry?.code }) }}</span>
                  </b-list-group-item>
                </b-list-group>
                <template #footer v-if="index === Object.keys(userSaleLines).length - 1">
                  <b-list-group flush>
                    <b-list-group-item class="d-flex justify-content-between" variant="info">
                      <span class="font-weight-bold">{{ $t('table.sales.title_total') }}</span>
                      <span>{{ formatNumber(totalSaleAmount, { precision: 2, marketCountry: userMarket?.supportCountry?.code }) }}</span>
                    </b-list-group-item>
                    <b-list-group-item class="d-flex justify-content-between" variant="info">
                      <span class="font-weight-bold">{{ $t('table.sales.title_total_share') }}</span>
                      <span>{{ formatNumber(totalShareAmount, { precision: 2, marketCountry: userMarket?.supportCountry?.code }) }}</span>
                    </b-list-group-item>
                  </b-list-group>
                </template>
              </b-card>
              <span v-if="!loader.isActive && !Object.keys(userSaleLines).length" class="no-table-data">
                {{ $t('table.general.no_data') }}
              </span>
            </market-card>
          </div>
        </b-col>

        <b-col v-if="userMarket" lg="4">
          <market-app-card
            v-if="userMarket.mobileAppAccess"
            :help-url="mobileAppHelpUrl"
            class="mb-4"
          />
          <market-card v-else class="mb-4">
            <p
              class="small text-muted mb-0"
              v-html="$t('pages.login.user.download_app_help_text', { app_help_link: mobileAppHelpUrl })"
            ></p>
          </market-card>
          <market-card v-if="userMarket.extraMarketInfo" class="mb-4">
            <p class="pre-wrap mb-0">{{ userMarket.extraMarketInfo }}</p>
          </market-card>
          <market-gallery-card
            v-if="userMarket.marketImages && userMarket.marketImages.length"
            :images="userMarket.marketImages"
            class="mb-4"
          />
          <market-contact-card
            :market="userMarket"
            :market-feedback="marketFeedback"
            class="mb-4"
          />
          <market-hours-card v-if="hasOpeningHours" :market="userMarket" class="mb-4" />
        </b-col>
      </b-row>
    </b-container>

    <request-campaign-modal
      modalId="request-campaign-add"
      v-if="userMarket && customerData" />
    <confirm-item-modal
      modalId="request-campaign-remove"
      :okButtonTitle="$t('modal.general.ok_button')"
      :cancelButtonTitle="$t('modal.general.cancel_button')"
      :title="$t('modal.product_sheets.title_request_campaign_removal')"
      :text="$t('modal.product_sheets.text_request_campaign_removal')"
      @onConfirm="confirmObjectAction(
        $_actionTypes.REQUEST_PRODUCT_CAMPAIGN_REMOVAL,
        'userNumber',
        $t('notifications.text.product_sheets.success_campaign_removal_requested'),
      )"/>
  </div>
</template>

<script>
  import SalesFilter from '@user/components/SalesFilter.vue'
  import ContentTable from '@base/components/ContentTable.vue'
  import MarketCard from '@base/components/market/MarketCard.vue'
  import MarketAppCard from '@base/components/market/MarketAppCard.vue'
  import MarketGalleryCard from '@base/components/market/MarketGalleryCard.vue'
  import MarketContactCard from '@base/components/market/MarketContactCard.vue'
  import MarketHoursCard from '@base/components/market/MarketHoursCard.vue'
  import { mapState } from 'vuex'
  import Formatting from '@base/mixins/Formatting'
  import TableUtils from '@base/mixins/TableUtils'
  import ObjectUtils from '@base/mixins/ObjectUtils.vue'
  import moment from 'moment'
  import {
    customerRequestInterceptor,
    customerResponseFulfilledInterceptor,
    customerResponseRejectedInterceptor
  } from '@base/store'
  import { marketFeedback } from '@base/services'
  import { MOBILE_APP_HELP_URLS, USED } from '@base/configs'
  import { SALES_SUMMARY_PERIODS } from '@user/configs'
  import ConfirmItemModal from "@base/components/ConfirmItemModal.vue";
  import RequestCampaignModal from "@user/components/RequestCampaignModal.vue";
  import ObjectAction from "@base/mixins/ObjectAction.vue";


  marketFeedback.interceptors.request.use(customerRequestInterceptor)
  marketFeedback.interceptors.response.use(
    customerResponseFulfilledInterceptor,
    customerResponseRejectedInterceptor
  )

  export const defaultSaleFilters = {
    dateStart: moment().startOf('month').format('YYYY-MM-DD'),
    dateEnd: moment().endOf('day').format('YYYY-MM-DD'),
    productType: null,
  }


  export default {
    name: 'SalesView',

    components: {
      RequestCampaignModal,
      ConfirmItemModal,
      ContentTable,
      SalesFilter,
      MarketCard,
      MarketAppCard,
      MarketGalleryCard,
      MarketContactCard,
      MarketHoursCard,
    },

    mixins: [ Formatting, TableUtils, ObjectAction, ObjectUtils ],

    data () {
      return {
        fields: [
          { key: 'period', label: this.$t('table.sales.period'), class: 'text-left' },
          { key: 'quantityTotal', label: this.$t('table.sales.quantity_total'), class: 'text-right' },
          { key: 'amountTotal', label: this.$t('table.sales.amount_total'), class: 'text-right' },
          { key: 'shareTotal', label: this.$t('table.sales.share'), class: 'text-right' },
        ],
        templates: [
          { name: 'period', field: 'period' },
          { name: 'quantity', field: 'quantityTotal' },
          { name: 'amount', field: 'amountTotal' },
          { name: 'share', field: 'shareTotal' }
        ],
        saleFilters: { ...defaultSaleFilters },
        loader: { isActive: true },
        marketFeedback: marketFeedback,
        usedProductType: USED,
        salesSummaryPeriods: SALES_SUMMARY_PERIODS,
      }
    },

    created() {
      this.getSaleSummary()
      if (Object.keys(this.customerData).length) {
        this.initSales()
      }
    },

    watch: {
      // When customer data is loaded, then we apply filters on sale data and collect it
      customerData () {
        this.initSales()
      },
    },

    computed: {
      ...mapState(['userMarket', 'customerData', 'userSaleLines', 'userSaleLineSummary']),

      mobileAppHelpUrl() {
        return MOBILE_APP_HELP_URLS[this.$i18n.locale.toUpperCase()] || MOBILE_APP_HELP_URLS.EN
      },

      posConnected () {
        return Boolean(this.userMarket && (
          this.userMarket.isFlealoverSoloFeature
          || this.userMarket.flexposApiValid
          || (this.userMarket.zettleIntegration && this.userMarket.zettleApiValid)
        ))
      },

      showProductSheets () {
        return Boolean(this.userMarket
          && this.userMarket.customerAccessToProductCreation
          && this.userMarket.individualizedItemsAccess
          && this.posConnected)
      },

      showBooking () {
        const bookingOpen = Boolean(this.userMarket
          && this.userMarket.onlineBookingFeature
          && this.userMarket.showPaymentTerms
          && (this.userMarket.gatewayApiValid || this.userMarket.allowOfflinePayments)
          && this.userMarket.customerAccessToBooking
          && this.posConnected)
        return bookingOpen && (this.showProductSheets || !this.userMarket.customerAccessToProductCreation)
      },

      canRequestCampaign () {
        return Boolean(this.userMarket
          && this.userMarket.customerAccessToCampaignRequests
          && this.userMarket.productCampaigns
          && this.userMarket.productCampaigns.length)
      },

      hasOpeningHours () {
        return Boolean(this.userMarket && (
          (this.userMarket.openingHours && this.userMarket.openingHours.length)
          || this.userMarket.openingHoursExtraInfo
        ))
      },

      totalSaleAmount () {
        let totalAmount = 0
        for (const key in this.userSaleLines) {
          totalAmount += this.getTotal(this.userSaleLines[key], 'totalAmount')
        }
        return totalAmount
      },

      totalShareAmount () {
        let totalShare = 0
        for (const key in this.userSaleLines) {
          totalShare += this.getTotal(this.userSaleLines[key], 'share')
        }
        return totalShare
      },
    },

    methods: {
      setDefaultProductType () {
        // If customer has one from two product types - then apply this filter to sales
        if (this.customerData.customerType.length === 1)
          this.saleFilters.productType = this.customerData.customerType[0]
      },

      getSaleLines () {
        return new Promise((resolve) => {
          this.loader = this.$loading.show(this.$_loaderOptions)
          this.$store.dispatch(this.$_actionTypes.GET_SALE_LINES_LIST)
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              this.loader.hide()
              resolve()
            })
        })
      },

      getSaleSummary () {
        return new Promise((resolve) => {
          this.$store.dispatch(this.$_actionTypes.GET_SALE_LINES_SUMMARY)
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              resolve()
            })
        })
      },

      updateSaleLinesWithFilter () {
        this.getSaleLines()
      },

      initSales () {
        this.setDefaultProductType()
        this.$store.commit(this.$_mutationTypes.SET_SALE_LINES_FILTERS, this.saleFilters)
        this.getSaleLines()
      },
    }
  }
</script>

<style scoped>
  .sales-hero {
    background: linear-gradient(180deg, var(--ui-brand-tint) 0%, var(--ui-brand-soft) 100%);
    padding: 1.5rem 0 3.5rem;
  }

  .seller-card {
    background: #fff;
    border: 1px solid var(--ui-border);
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: var(--ui-shadow);
    margin-bottom: -3rem;
    position: relative;
  }

  .seller-card__head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  .seller-card__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--ui-ink);
    margin-bottom: 0.25rem;
  }

  .seller-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1.25rem;
    color: var(--ui-muted);
    font-size: 0.95rem;
  }

  .seller-card__meta li {
    display: inline-flex;
    align-items: center;
  }

  .seller-card__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .seller-card__tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.75rem;
  }

  .stat-tile {
    background: var(--ui-surface);
    border-radius: 10px;
    padding: 0.875rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .stat-tile--wide {
    grid-column: 1 / -1;
  }

  .stat-tile__label {
    font-size: 0.8125rem;
    color: var(--ui-muted);
  }

  .stat-tile__value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--ui-ink);
    line-height: 1.2;
  }

  .stat-tile__hint {
    font-size: 0.8125rem;
    color: var(--ui-muted);
  }

  .stat-tile__text {
    margin-bottom: 0.5rem;
    color: var(--ui-ink);
  }

  .sales-page__body {
    padding-top: 4.5rem;
  }

  .sale-day {
    border-color: var(--ui-border);
    border-radius: 8px;
  }

  .total-sale-footer-container {
    padding: 0 !important;
  }

  @media (min-width: 768px) {
    .seller-card {
      padding: 1.5rem;
    }

    .seller-card__title {
      font-size: 1.75rem;
    }

    .seller-card__tiles {
      grid-template-columns: repeat(3, 1fr);
    }

    .stat-tile--wide {
      grid-column: span 2;
    }

    .seller-card__actions {
      flex-wrap: nowrap;
    }
  }

  @media (max-width: 575.98px) {
    .seller-card__actions {
      width: 100%;
    }

    .seller-card__actions .btn {
      flex: 1 1 auto;
    }
  }
</style>
